<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <div v-if="isLoading" class="text-center text-gray-600 mt-4">
      Chargement en cours...
    </div>

    <div v-else class="mb-8">
      <!-- Toolbar modernisée avec icônes -->
      <div class="anamnesis-detail-toolbar">
        <div class="anamnesis-detail-toolbar-content">
          <!-- Section titre avec icône et gestion des permissions -->
          <div class="anamnesis-detail-title-section">
            <!-- Icône de projet -->
            <div class="flex items-center mr-3">
              <FileTextIcon class="h-6 w-6 text-blue-600" />
            </div>

            <div class="relative flex-1">
              <!-- Édition du titre -->
              <input v-if="isEditingTitle && canEdit" ref="titleInput" v-model="editedTitle" type="text"
                placeholder="Nom du projet" class="anamnesis-detail-title-edit" @blur="handleTitleUpdate"
                @keydown.enter="handleTitleUpdate" @keydown.escape="cancelTitleEdit" />

              <!-- Affichage normal -->
              <h1 v-else class="anamnesis-detail-title" :class="{ readonly: !canEdit }"
                @click="canEdit && startTitleEdit()">
                {{ project?.acronym || project?.title || 'Sans titre' }}
              </h1>
              <div v-if="project?.acronym && project?.title" class="text-sm text-gray-500 mt-0.5">
                {{ project.title }}
              </div>
            </div>
          </div>

          <!-- Actions avec gestion des permissions -->
          <div class="anamnesis-detail-actions">
            <!-- Toggle public/privé -->
            <button class="anamnesis-detail-status" :class="[
              project?.is_public ? 'public' : 'private',
              { disabled: !canEdit }
            ]" :disabled="!canEdit" @click="canEdit && handlePublicToggle()">
              <EyeIcon v-if="project?.is_public" class="h-4 w-4" />
              <EyeOffIcon v-else class="h-4 w-4" />
              {{ project?.is_public ? 'Public' : 'Privé' }}
            </button>

            <!-- Boutons d'action - seulement visibles pour les propriétaires/admin -->
            <template v-if="canEdit">
              <!-- Boutons d'édition -->
              <template v-if="isEditing">
                <button class="anamnesis-detail-btn-success" @click="handleSave">
                  <CheckIcon class="h-5 w-5" />
                  Valider
                </button>
                <button class="anamnesis-detail-btn-secondary" @click="handleCancel">
                  <XIcon class="h-5 w-5" />
                  Annuler
                </button>
              </template>
              <button v-else class="anamnesis-detail-btn-primary" @click="handleEdit">
                <PencilIcon class="h-5 w-5" />
              </button>

              <!-- Supprimer - seulement pour les propriétaires/admin -->
              <button v-if="canDelete" class="anamnesis-detail-btn-delete" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Grille principale avec fond blanc et cadres -->
      <div class="anamnesis-detail-grid">
        <!-- Colonne de gauche : Détails -->
        <div class="anamnesis-detail-section bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Informations du projet
          </h3>

          <!-- Acronyme -->
          <div class="anamnesis-detail-field">
            <HashIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Acronyme</label>
              <div v-if="isEditing">
                <input v-model="editedProject.acronym" class="anamnesis-detail-field-input"
                  placeholder="Acronyme du projet" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ project?.acronym || 'Non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Type -->
          <div class="anamnesis-detail-field">
            <FolderIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div v-if="isEditing">
                <input v-model="editedProject.type" class="anamnesis-detail-field-input" placeholder="Type de projet" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ project?.type || 'Non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Titre complet -->
          <div class="anamnesis-detail-field">
            <FileTextIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Titre complet</label>
              <div v-if="isEditing">
                <input v-model="editedProject.title" class="anamnesis-detail-field-input"
                  placeholder="Titre complet du projet" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ project?.title || 'Non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Description -->
          <div class="anamnesis-detail-field">
            <AlignLeftIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <div v-if="isEditing">
                <textarea v-model="editedProject.description" rows="3" class="anamnesis-detail-field-input"
                  placeholder="Description du projet"></textarea>
              </div>
              <p v-else class="anamnesis-detail-field-text whitespace-pre-wrap">
                {{ project?.description || 'Non spécifié' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Colonne de droite : Métadonnées -->
        <div class="anamnesis-detail-metadata bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Métadonnées</h3>

          <!-- Métadonnées -->
          <div class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Créé le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(project?.created_at) }}
              </p>
            </div>
          </div>

          <div v-if="project?.updated_at && project.updated_at !== project.created_at"
            class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Modifié le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(project?.updated_at) }}
              </p>
            </div>
          </div>

          <div v-if="detailedMemos.length" class="anamnesis-detail-meta-item">
            <ClipboardIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Mémos associés</p>
              <p class="anamnesis-detail-meta-text">
                {{ detailedMemos.length }} mémo(s)
              </p>
            </div>
          </div>

          <div class="anamnesis-detail-meta-item">
            <UserIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Propriétaire</p>
              <p class="anamnesis-detail-meta-text">
                {{ ownerName || 'Chargement...' }}
              </p>
            </div>
          </div>

          <!-- Section des références scientifiques -->
          <div class="border-t border-gray-200 mt-4 pt-4">
            <h4 class="text-sm font-medium text-gray-700 mb-3">Références</h4>

            <!-- ID AureHAL -->
            <div class="anamnesis-detail-field mb-3">
              <HashIcon class="anamnesis-detail-field-icon h-4 w-4" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">ID AureHAL</label>
                <div v-if="isEditing">
                  <input v-model="editedProject.aurehal_id" class="anamnesis-detail-field-input text-sm"
                    placeholder="ID AureHAL" />
                </div>
                <p v-else class="anamnesis-detail-field-text text-sm">
                  {{ project?.aurehal_id || 'Non spécifié' }}
                </p>
              </div>
            </div>

            <!-- Référence scientifique -->
            <div class="anamnesis-detail-field mb-3">
              <BookmarkIcon class="anamnesis-detail-field-icon h-4 w-4" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Référence scientifique</label>
                <div v-if="isEditing">
                  <input v-model="editedProject.link" class="anamnesis-detail-field-input text-sm"
                    placeholder="Lien de référence (DOI, URL, etc.)" />
                </div>
                <p v-else class="anamnesis-detail-field-text text-sm">
                  {{ project?.link || 'Non spécifié' }}
                </p>
              </div>
            </div>

            <!-- Financeur -->
            <div class="anamnesis-detail-field">
              <BuildingIcon class="anamnesis-detail-field-icon h-4 w-4" />
              <div class="anamnesis-detail-field-content">
                <label class="block text-sm font-medium text-gray-700 mb-1">Financeur</label>
                <div v-if="isEditing">
                  <input v-model="editedProject.fundedby" class="anamnesis-detail-field-input text-sm"
                    placeholder="Financeur du projet" />
                </div>
                <p v-else class="anamnesis-detail-field-text text-sm">
                  {{ project?.fundedby || 'Non spécifié' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Mémos avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <h2 class="anamnesis-detail-memos-title">Mémos associés</h2>
        <div v-if="detailedMemos.length">
          <MemosSubset :memos="detailedMemos" :show-delete-button="false" @view="handleMemoView" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucun mémo associé à ce projet
        </div>
      </div>
      
      <!-- Message d'accès restreint -->
      <div v-else class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <div class="text-center py-8">
          <EyeOffIcon class="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
          <p class="text-gray-600">
            {{ !authStore.isAuthenticated ? 'Connectez-vous pour voir les mémos associés' : 'Cet asset privé ne vous appartient pas' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Bouton de retour en haut -->
    <ScrollToTop />

    <Notification ref="notification" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer le projet ?"
      :message="`Êtes-vous sûr de vouloir supprimer le projet '${project?.acronym || project?.title}' ? Cette action est irréversible.`"
      confirm-text="Supprimer" cancel-text="Annuler" @confirm="confirmDelete" />
  </div>
</template>

<script setup lang="ts">
import {
    AlignLeft as AlignLeftIcon,
    Bookmark as BookmarkIcon,
    Building as BuildingIcon,
    Check as CheckIcon,
    Clipboard as ClipboardIcon,
    Clock as ClockIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    FileText as FileTextIcon,
    Folder as FolderIcon,
    Hash as HashIcon,
    Pencil as PencilIcon,
    Trash as TrashIcon,
    User as UserIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchMemosUuid } from '../../api/memos';
import { deleteProject, fetchProjectWithMemos, projects, updateProject } from '../../api/projects';
import type { Memo } from '../../api/types';
import type { ProjectWithMemos } from '../../api/types/';
import { useAssetsCacheStore } from '../../stores/assetsCache';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import { useUserNamesCacheStore } from '../../stores/userNamesCache';
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import MemosSubset from '../subsets/MemosSubset.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Props
interface Props {
  uuid: string;
}

const props = defineProps<Props>();

// Composables
const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();

// État principal
const project = ref<ProjectWithMemos | null>(null);
const detailedMemos = ref<Memo[]>([]);
const isLoading = ref(true);
const isEditing = ref(false);
const isEditingTitle = ref(false);
const showDeleteModal = ref(false);
const ownerName = ref<string>('');

// Références
const notification = ref<InstanceType<typeof Notification> | null>(null);
const titleInput = ref<HTMLInputElement | null>(null);

// État d'édition
interface ProjectEditData {
  title?: string;
  acronym?: string;
  type?: string;
  description?: string;
  aurehal_id?: string;
  fundedby?: string;
  link?: string;
  is_public?: boolean;
  user_id: string;
}

const editedProject = ref<ProjectEditData>({
  title: '',
  acronym: '',
  type: '',
  description: '',
  aurehal_id: '',
  fundedby: '',
  link: '',
  is_public: false,
  user_id: authStore.user?.uuid || ''
});
const editedTitle = ref('');

// Gestion des permissions
const isMyAsset = computed((): boolean => {
  return project.value?.user_id === authStore.user?.uuid;
});

const canEdit = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canDelete = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canViewSubsets = computed((): boolean => {
  // Peut voir les subsets si :
  // 1. L'asset est public, OU
  // 2. L'utilisateur est connecté ET est propriétaire de l'asset
  return project.value?.is_public || (authStore.isAuthenticated && isMyAsset.value);
});



// Récupérer l'état depuis history.state
const historyState = window.history.state;
project.value = historyState?.project || null;

onMounted(async () => {
  await loadProject();
});

const loadDetailedMemos = async (): Promise<void> => {
  if (!project.value?.memos || project.value.memos.length === 0) {
    detailedMemos.value = [];
    return;
  }

  try {
    const memoPromises = project.value.memos.map(memo =>
      fetchMemosUuid(memo.uuid)
    );
    const memosData = await Promise.all(memoPromises);
    detailedMemos.value = memosData.map(data => data[0] as Memo);
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des mémos:', error);
  }
};

const loadProject = async (): Promise<void> => {
  if (project.value) {
    updateBreadcrumb();
    resetEditedData();
    await loadDetailedMemos();
    await loadOwnerName();
    isLoading.value = false;
    return;
  }

  const uuid = props.uuid;
  if (!uuid) {
    if (authStore.isAuthenticated) {
      await router.push('/myprojects');
    } else {
      await router.push('/');
    }
    return;
  }

  try {
    let projectData;
    // Si l'utilisateur est connecté, utiliser l'API normale
    if (authStore.isAuthenticated) {
      projectData = await fetchProjectWithMemos(uuid);
    } else {
      // Sinon, utiliser l'API publique qui permet l'accès par UUID
      projectData = await projects.getPublicByUuidWithMemos(uuid);
    }
    project.value = projectData;
    resetEditedData();
    updateBreadcrumb();
    await loadDetailedMemos();
    await loadOwnerName();
  } catch (error) {
    console.error('Erreur lors du chargement du projet:', error);
    notification.value?.showNotification('Erreur lors du chargement du projet', 'error');
    if (authStore.isAuthenticated) {
      await router.push('/myprojects');
    } else {
      await router.push('/');
    }
  } finally {
    isLoading.value = false;
  }
};

const loadOwnerName = async (): Promise<void> => {
  if (!project.value?.user_id) {
    ownerName.value = 'Inconnu';
    return;
  }

  try {
    const name = await userNamesCacheStore.getUserNameAsync(project.value.user_id);
    ownerName.value = name || 'Utilisateur supprimé';
  } catch (error) {
    console.error('Erreur lors du chargement du nom du propriétaire:', error);
    ownerName.value = 'Erreur de chargement';
  }
};

const updateBreadcrumb = (): void => {
  const projectName = project.value?.acronym || project.value?.title || 'Projet';
  const breadcrumbPath = authStore.isAuthenticated ? '/myprojects' : '/';
  const breadcrumbName = authStore.isAuthenticated ? 'Mes projets' : 'Accueil';
  breadcrumbsStore.updateBreadcrumbs([
    { name: breadcrumbName, path: breadcrumbPath },
    { name: projectName, path: route.fullPath }
  ]);
};

const resetEditedData = (): void => {
  if (project.value) {
    editedProject.value = {
      title: project.value.title,
      acronym: project.value.acronym,
      type: project.value.type,
      description: project.value.description,
      aurehal_id: project.value.aurehal_id,
      fundedby: project.value.fundedby,
      link: project.value.link,
      is_public: project.value.is_public,
      user_id: project.value.user_id
    };
    editedTitle.value = project.value.title || '';
  }
};

const handleEdit = (): void => {
  if (!canEdit.value) return;
  resetEditedData();
  isEditing.value = true;
};

const handleCancel = (): void => {
  isEditing.value = false;
  isEditingTitle.value = false;
  resetEditedData();
};

const handleSave = async (): Promise<void> => {
  if (!project.value || !canEdit.value) return;

  try {
    // Mettre à jour le projet
    const updatedProject = await updateProject(project.value.uuid, editedProject.value);

    // Mettre à jour l'objet local
    project.value = { ...updatedProject, memos: project.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('projects', project.value);

    isEditing.value = false;
    isEditingTitle.value = false;

    updateBreadcrumb();
    notification.value?.showNotification('Projet mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du projet:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handlePublicToggle = async (): Promise<void> => {
  if (!project.value || !canEdit.value) return;

  try {
    const updatedProject = await updateProject(project.value.uuid, {
      ...project.value,
      is_public: !project.value.is_public
    });
    project.value = { ...updatedProject, memos: project.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('projects', project.value);

    notification.value?.showNotification('Visibilité mise à jour', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la visibilité:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour de la visibilité', 'error');
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Non spécifié';
  return new Date(dateString).toLocaleDateString('fr-FR');
};

const handleMemoView = (memo: Memo): void => {
  router.push(`/memos/${memo.uuid}`);
};

const startTitleEdit = (): void => {
  if (!canEdit.value) return;
  isEditingTitle.value = true;
  editedTitle.value = project.value?.title || '';

  nextTick((): void => {
    titleInput.value?.focus();
  });
};

const cancelTitleEdit = (): void => {
  isEditingTitle.value = false;
  if (project.value) {
    editedTitle.value = project.value.title || '';
  }
};

const handleTitleUpdate = async (): Promise<void> => {
  if (!project.value || !canEdit.value || editedTitle.value === project.value.title) {
    isEditingTitle.value = false;
    return;
  }

  try {
    const updatedProject = await updateProject(project.value.uuid, {
      title: editedTitle.value.trim(),
      user_id: project.value.user_id
    });

    // Mettre à jour l'objet local
    project.value = { ...updatedProject, memos: project.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('projects', project.value);

    isEditingTitle.value = false;
    updateBreadcrumb();
    notification.value?.showNotification('Titre mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du titre:', error);
    notification.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleDelete = (): void => {
  if (!canDelete.value) return;
  showDeleteModal.value = true;
};

const confirmDelete = async (): Promise<void> => {
  if (!project.value || !canDelete.value) return;

  try {
    await deleteProject(project.value.uuid);

    // Supprimer du cache
    assetsCacheStore.removeAssetFromCache('projects', project.value.uuid);

    notification.value?.showNotification('Projet supprimé avec succès', 'success');
    router.push('/myprojects');
  } catch (error) {
    console.error('Erreur lors de la suppression du projet:', error);
    notification.value?.showNotification('Erreur lors de la suppression', 'error');
  }
  showDeleteModal.value = false;
};
</script>

<style>
/* Les styles sont définis dans main.css avec les classes anamnesis-detail-* */
</style>